import { GetContract } from '@/services/fhir/contract/GetContract';
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useOrganizationContext } from './OrganizationContext';
import { Contract } from 'fhir/r5';

type ContractResource = Contract;

interface MedicalReportsAgentContextValue {
  contractMedicalReports: ContractResource | null;
  provider: 'openai' | 'gemini' | null;
  apiKey: string | null;
  refetchContract: () => Promise<void>;
}

interface MedicalReportsAgentContextProviderProps {
  children: React.ReactNode;
}

export const MedicalReportsAgentContext = createContext<MedicalReportsAgentContextValue>({} as MedicalReportsAgentContextValue);

export function MedicalReportsAgentContextProvider({ children }: MedicalReportsAgentContextProviderProps) {
  const { organization } = useOrganizationContext();
  const [contractMedicalReports, setContractMedicalReports] = useState<ContractResource | null>(null);
  const [provider, setProvider] = useState<'openai' | 'gemini' | null>(null);
  const [apiKey, setApiKey] = useState<string | null>(null);

  const fetchContract = useCallback(async () => {
    try {
      if (!organization?.id) {
        console.warn("Nenhuma organização selecionada");
        setContractMedicalReports(null);
        setProvider(null);
        setApiKey(null);
        return;
      }

      const contractData = await GetContract({
        organization: organization.id,
        count: 1,
        sort: true,
        status: "executed",
        identifier: "MEDICAL_REPORTS_AGENT",
      });
      
      if (contractData?.entry?.[0]?.resource) {
        const resource = contractData.entry[0].resource;
        setContractMedicalReports(resource);

        const apiKeyExtension = resource.extension?.find(
          (ext: any) => ext.url === "https://fluxmed.com.br/fhir/extension/medicalreportsagent/api-key"
        );
        const providerExtension = resource.extension?.find(
          (ext: any) => ext.url === "https://fluxmed.com.br/fhir/extension/medicalreportsagent/provider"
        );

        if (apiKeyExtension?.valueString && providerExtension?.valueString) {
          setProvider(providerExtension.valueString as 'openai' | 'gemini');
          setApiKey(apiKeyExtension.valueString);
        } else {
          setProvider(null);
          setApiKey(null);
        }
      } else {
        setContractMedicalReports(null);
        setProvider(null);
        setApiKey(null);
      }
    } catch (error) {
      console.error("Erro ao buscar contrato:", error);
      setContractMedicalReports(null);
      setProvider(null);
      setApiKey(null);
    }
  }, [organization]);

  useEffect(() => {
    fetchContract();
  }, [fetchContract]);

  const contextValue: MedicalReportsAgentContextValue = {
    contractMedicalReports,
    provider,
    apiKey,
    refetchContract: fetchContract,
  };

  return (
    <MedicalReportsAgentContext.Provider value={contextValue}>
      {children}
    </MedicalReportsAgentContext.Provider>
  );
} 

export function useMedicalReportsAgentContext() {
  return useContext(MedicalReportsAgentContext);
} 